import {
    IPlaylist, IPlaylistHot,
    IPlaylistMedia, IPlaylistMediaUpdated,
    IPlaylistMediaUpload,
    IPlaylistStripe,
    IPlaylistStripeUpdated,
    IPlaylistStripeUpload
} from '@interfaces/playlist';
import {MediaTypeEnum} from '@shared/enums/media-type.enum';
import * as moment from 'moment';

export class PlaylistModel {

    // Order visual media by position, skip stripe
    static setVisualMediaOrder(plMedia: Array<IPlaylistMedia | IPlaylistStripe>): IPlaylistMedia[] {
        const filteredMedia = plMedia.filter((media) => !media.hasOwnProperty('stripe')) as IPlaylistMedia[]; // skip text media;
        return PlaylistModel.setOrderPlaylistMedia(filteredMedia);
    }

    static setOrderPlaylistMedia(arr: IPlaylistMedia[]): IPlaylistMedia[] {
        const data = arr.map((media, i) => {
            return {...media, sequence: i};
        });
        return PlaylistModel.getOrderedPlaylistMedia(data);
    }

    static getOrderedPlaylistMedia(arr: IPlaylistMedia[]): IPlaylistMedia[] {
        return arr.sort((a, b) => a.sequence - b.sequence);
    }

    // set widget into container, should be at single exemplar, at 1-st place
    static setWidgetIntoVisualContainer(arr: IPlaylistMedia[], newWidget: IPlaylistMedia): IPlaylistMedia[] {
        const filteredArr = arr.filter((el) => el.media.mediaType !== MediaTypeEnum.widget);
        return PlaylistModel.setVisualMediaOrder([newWidget, ...filteredArr]);
    }

    /* Media with same id should count one time */
    static getPlaylistSize(plMedia: IPlaylistMedia[]): number {
        const uniquePlMedia = new Map<number, number>();
        plMedia.forEach((m) => uniquePlMedia.set(m.media.id, m.media.size));
        return Array.from(uniquePlMedia.entries()).reduce((total, size) => {
            return size[1] + total;
        }, 0);
    }

    static getPlaylistDuration(plMedia: IPlaylistMedia[]): number {
        return plMedia.reduce((acc, item) => {
            return (item.duration || 0) + acc;
        }, 0);
    }

    static newPlaylistMedia(plMedia: IPlaylistMedia[]): IPlaylistMediaUpload[] {
        const arr = plMedia.filter((media) => !media.hasOwnProperty('id'));
        return arr.map((media) => {
            return {
                duration: media.duration,
                sequence: media.sequence,
                media: media.media.id,
                ...(media.mediaType !== undefined && { mediaType: media.mediaType }),
                chanel: media.chanel
            };
        });
    }

    static getUpdatedMedia(oldPlaylistMedia: IPlaylistMedia[], droppedMedias: IPlaylistMedia[]): IPlaylistMediaUpdated[] {
        // Medias that exist in DB
        const selectedMedias = droppedMedias.filter((media) => media.hasOwnProperty('id'));

        const oldArr = oldPlaylistMedia.map((m) => JSON.stringify(m));
        // Compare if dropped medias has been changed
        const mediaToUpdate = selectedMedias.filter((dropped) => !oldArr.includes(JSON.stringify(dropped)));

        return mediaToUpdate.map((media) => {
            return {
                duration: media.duration,
                sequence: media.sequence,
                chanel: media.chanel,
                playlistMediaId: media.id as number
            };
        });
    }

    static newStripe(plStripe: IPlaylistStripe): IPlaylistStripeUpload[] {
         if (!plStripe.hasOwnProperty('id') ) {
             const {id, playlist, stripe, ...rest} = {...plStripe};
             return [{...rest, stripe: stripe.id}];
         }
         return [];
    }

    static getUpdatedStripe(plStripe: IPlaylistStripe): IPlaylistStripeUpdated[] {
        if (plStripe.hasOwnProperty('id')) {
            const {id, playlist, stripe, ...rest} = {...plStripe};
            return [{...rest, playlistStripeId: stripe.id}];
        }
        return [];
    }

    // check if playlist media can be sortable in chanel
    static isMediaSortRestricted(media: IPlaylistMedia): boolean {
        return media.media.mediaType === MediaTypeEnum.widget;
    }

    // make new copy of playlist
    static copyPlaylist<T extends IPlaylist | IPlaylistHot>(playlist: T): T {
        const newTitle = playlist.name + ' Copy ' + moment().format('YYYY-MM-DD HH:mm:ss');
        const medias: IPlaylistMedia[] = playlist.medias.map((plMedia) => {
            const {id, playlist, ...rest} = plMedia;

            // add mediaType for widget to avoid error issues when media duration is 0
            const mediaType = plMedia.media.mediaType === MediaTypeEnum.widget
                ? MediaTypeEnum.widget
                : rest.mediaType;

            return {
                ...rest,
                mediaType,
            };
        });

        const stripe: IPlaylistStripe[] = playlist.stripes.map((plStripe) => {
            const {id, playlist, ...rest} = plStripe; // unbind playlistStripe from existing playlist
            return {...rest};
        })

        return Object.assign({}, playlist, {medias: medias}, {stripes: stripe}, {name: newTitle});
    }

    // Playlist can't be empty or contains only rss, widget or text
    static isPlaylistContentFulfilled(visual: IPlaylistMedia[], audio: IPlaylistMedia[]): boolean {
        const visualContent = visual.filter((media) => {
            return media.media.mediaType !== MediaTypeEnum.widget;
        }); // skip widget
        return Boolean([...visualContent, ...audio].length);
    }

    static getMediasToDelete(oldPlaylist: IPlaylist, droppedMedias: IPlaylistMedia[]): number[] {
        const droppedMediasIdsFromDB = droppedMedias.filter((m) => m.hasOwnProperty('id')).map((m) => m.id);
        const mediasToDelete = oldPlaylist.medias.filter((m) => !droppedMediasIdsFromDB.includes(m.id));
        return mediasToDelete.map((m) => m.id as number);
    }

    static getStripeToDelete(oldPlaylist: IPlaylist, stripe: IPlaylistStripe | null): number[] {
        const oldPlaylistStripeID = oldPlaylist.stripes.length ? oldPlaylist.stripes[0].id : null;
        if (oldPlaylistStripeID && oldPlaylistStripeID !== stripe?.id) return [oldPlaylistStripeID];
        return [];
    }
}
